// Single source of truth for `external_lookups` data on the client.
//
// Before this hook existed, FOUR separate components (SourceStatusStrip,
// ExternalSourcesPanel, OpenItemsSummary, and PersonPage's externalsPending
// effect) each fetched + polled `/api/external/jobs/:jobId` independently.
// That meant 4× the network traffic, four sources of truth that could fall
// out of sync, and four implementations of the same poll-while-active logic.
//
// Now PersonPage calls this hook once and passes `latest` (and the
// `enqueue` / `refresh` actions where needed) down as props. The hook:
//
//   1. Fetches once on mount.
//   2. Subscribes to /events SSE and merges `external_updated` messages
//      into local state — near-realtime updates with no polling under
//      steady state.
//   3. Falls back to a slow 30s safety-net poll while any source is in
//      a non-terminal state (queued/running). Catches missed SSE messages
//      after a reconnect or stale event-source.
//   4. Exposes `enqueue(source)` for re-running a source and `refresh()`
//      for explicit refetches.

import { useCallback, useEffect, useState } from "react";
import type { ExternalLookup, ExternalSource } from "../components/ExternalSourcesPanel";
import type { ServerEvent } from "../types";

export type ExternalLookupsMap = Partial<Record<ExternalSource, ExternalLookup | null>>;

interface ApiResponse {
  latest: ExternalLookup[];
  all?: ExternalLookup[];
}

export interface UseExternalLookupsResult {
  /** Per-source latest lookup. Sparse — sources we haven't seen yet
   *  are missing keys (rather than null), so consumers can tell
   *  "haven't tried" from "tried but no row" via missing vs. null. */
  latest: ExternalLookupsMap;
  /** True until the first fetch completes successfully (or fails). */
  loading: boolean;
  /** Enqueue a fresh lookup for a source. Resolves once the server has
   *  inserted the row + the local `latest` map has been refreshed. */
  enqueue: (source: ExternalSource) => Promise<void>;
  /** Force-refetch the entire latest map. Useful after admin actions
   *  that mutate state outside the normal SSE flow. */
  refresh: () => Promise<void>;
}

export function useExternalLookups(jobId: string): UseExternalLookupsResult {
  const [latest, setLatest] = useState<ExternalLookupsMap>({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/external/jobs/${jobId}`, { credentials: "include" });
      if (!res.ok) return;
      const data = (await res.json()) as ApiResponse;
      const next: ExternalLookupsMap = {};
      for (const row of data.latest ?? []) {
        if (row && typeof row.source === "string") {
          next[row.source as ExternalSource] = row;
        }
      }
      setLatest(next);
    } catch {
      // Silent on transient network errors — the safety-net poll + SSE
      // will recover. Components default to "loading" / empty state which
      // is acceptable here.
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Initial fetch + SSE subscription. One EventSource per hook mount;
  // the hook is intended to be called once at the page level (PersonPage),
  // so this is a single connection per person page — same cost as the
  // SSE PersonPage already opens for job_deleted events. Acceptable.
  useEffect(() => {
    let alive = true;
    refresh();

    const es = new EventSource("/events");
    es.onmessage = (msg) => {
      if (!alive) return;
      try {
        const evt = JSON.parse(msg.data) as ServerEvent;
        if (evt.type !== "external_updated") return;
        if (evt.lookup.person_job_id !== jobId) return;
        // Merge the updated row into our map. Cast to ExternalLookup —
        // the server-event variant is loose-typed to avoid circular
        // imports, but the runtime shape matches what the panel reads.
        const updated = evt.lookup as unknown as ExternalLookup;
        setLatest((prev) => ({ ...prev, [updated.source]: updated }));
      } catch {
        // Bad JSON or shape — ignore. Safety-net poll recovers if
        // we miss state updates this way.
      }
    };

    return () => {
      alive = false;
      es.close();
    };
  }, [jobId, refresh]);

  // Safety-net poll — only runs while there's still in-flight work.
  // SSE is the primary update channel; this exists to catch missed
  // messages after a transient network blip or a server restart.
  // 30s cadence is plenty for "did we miss something" — for live
  // updates the SSE handler above gets us within ~100ms of the event.
  useEffect(() => {
    const anyActive = Object.values(latest).some(
      (r) => r && (r.status === "queued" || r.status === "running"),
    );
    if (!anyActive) return;
    const id = setInterval(() => {
      refresh();
    }, 30_000);
    return () => clearInterval(id);
  }, [latest, refresh]);

  const enqueue = useCallback(
    async (source: ExternalSource) => {
      const res = await fetch(`/api/external/jobs/${jobId}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ source }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Optimistically refetch — SSE will also fire but a manual refetch
      // makes the UI feel more responsive after a click.
      await refresh();
    },
    [jobId, refresh],
  );

  return { latest, loading, enqueue, refresh };
}
